import { Component, Input, computed, inject, signal } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { filter, map } from 'rxjs';

import { OrderType, SearchViewModel } from '@models/module/grid';
import { OpenDocumentsTableRow } from '@models/module/openDocuments/openDocumentsModel';
import { OpenDocumentsTableService } from '@module-instance/open-documents-table/open-documents-table.service';
import {
  CipoTableOptions,
  DataListCommandFilterType,
  FiltersToApply,
  LocalFilterService,
  TablePaginator,
} from '@shared/components/data-list/common';
import { DataListModule } from '@shared/components/data-list/data-list.module';
import { DEFAULT_INFINITE_PAGE, PAGE_SIZE_OPTIONS, PRESET_BG_COLOR, PRESET_COLOR } from '@shared/consts';
import { DocumentService } from '@shared/services/document.service';
import { UserStore } from '@signal-store/user.store';

@Component({
  selector: 'app-open-documents-table',
  templateUrl: './open-documents-table.component.html',
  imports: [DataListModule],
})
export class OpenDocumentsTableComponent {
  private openDocumentsService = inject(OpenDocumentsTableService);
  private localFilterService = inject(LocalFilterService);
  private userStore = inject(UserStore);
  private documentService = inject(DocumentService);

  isinfiniteScroll = computed(() => this.userStore.userData().loadMore);
  instanceId: number;
  searchOptions: SearchViewModel = {
    page: 1,
    pagesize: this.isinfiniteScroll() ? DEFAULT_INFINITE_PAGE : PAGE_SIZE_OPTIONS.long[2],
  };
  filters: DataListCommandFilterType[] = [];
  tableColumns = this.openDocumentsService.getTableColumns();
  tableData = signal<OpenDocumentsTableRow[]>(undefined);
  /* We don't have actions on this table, so we use any for action type */
  tableOptions = signal<CipoTableOptions<any, OpenDocumentsTableRow>>({
    clickableRows: true,
    serverPaginationSort: true,
    pagination: {
      pageIndex: 0,
      pageSize: this.searchOptions.pagesize,
      pageSizeOptions: PAGE_SIZE_OPTIONS.long,
    },
  });

  @Input('entityinstance')
  set _entityInstance(instance: any) {
    this.instanceId = instance.entityInstanceId;
    this.populateTableData();
    this.openDocumentsService
      .getDocumentsModules(this.instanceId)
      .subscribe(modules => (this.filters = this.openDocumentsService.initFilters(modules)));
  }

  openPopup(element: OpenDocumentsTableRow) {
    const { contractId, paymentPeriodId, entityInstanceId, moduleId, screenId, workflowId } = element;
    this.documentService.openDocument({
      moduleId,
      contractId,
      entityInstanceId,
      paymentPeriodId,
      screenId,
      workflowId,
    });
  }

  populateTableData() {
    this.openDocumentsService
      .getOpenDocuments(this.searchOptions, this.instanceId)
      .pipe(
        filter(data => !!data),
        map(({ data, records }) => ({
          records,
          data: data.map(row => {
            const tableRow: OpenDocumentsTableRow = {
              ...row,
              state: {
                bgColor: row.isInApproval ? PRESET_BG_COLOR : row.stateColor,
                color: row.isInApproval ? PRESET_COLOR : row.stateTextColor,
                label: row.isInApproval ? 'defaultStatus.InApproval' : row.state,
              },
            };
            return tableRow;
          }),
        })),
      )
      .subscribe(({ data, records }) => {
        this.tableData.set(data);
        this.tableOptions.update(options => {
          options.pagination.pageIndex = this.searchOptions.page - 1;
          options.pagination.length = records;
          options.pagination.pageSize = this.searchOptions.pagesize;
          return { ...options };
        });
      });
  }

  search({ filters, criteria }: FiltersToApply) {
    this.searchOptions.criteria = criteria;
    this.searchOptions.filter = this.localFilterService.mapLocalFilters(filters);
    this.searchOptions.page = 1;
    this.tableData.set(null);
    this.tableOptions.update(options => {
      options.pagination.pageIndex = 0;
      return options;
    });

    this.populateTableData();
  }

  handleSort(event: Sort) {
    this.searchOptions = {
      ...this.searchOptions,
      sort: [
        {
          field: event.active,
          order: event.direction === 'asc' ? OrderType.Ascending : OrderType.Descending,
        },
      ],
      page: 1,
    };
    this.tableData.set(undefined);

    this.populateTableData();
  }

  handlePagination({ pageIndex, pageSize }: TablePaginator) {
    // If the page size is different from the default one, we need to update the page size. Infinite page size doesn't need to be updated.
    const pagesize = pageSize && pageSize !== this.searchOptions.pagesize ? pageSize : this.searchOptions.pagesize;
    this.searchOptions = {
      ...this.searchOptions,
      page: pageIndex + 1,
      pagesize,
    };

    this.populateTableData();
  }
}
